import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'

import { toast } from 'react-toastify'
import { sanitizeImageUrl } from '../../helpers/sanitizeUrl.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useGlobalFileDrag } from '../hooks/useGlobalFileDrag'
import * as archivoAdjuntoService from '../servicios/archivoAdjuntoService'
import * as publicacionesService from '../servicios/publicacionesService'
import * as publicacionesTemporalesService from '../servicios/publicacionesTemporalesService'
import LinkButton from '../ui/LinkButton'
import styles from './Upload.module.css'

const Upload = ({ idPublicacion, idPublicacionTemporal, accept }) => {
  const [adjuntos, setAdjuntos] = useState([])
  const [reload, setReload] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [isOverDropZone, setIsOverDropZone] = useState(false)

  const { isDraggingFile } = useGlobalFileDrag()

  useEffect(() => {
    if (idPublicacion > 0 || idPublicacionTemporal > 0) {
      obtenerAdjuntos()
    }
    setReload(false)
  }, [idPublicacion, idPublicacionTemporal, reload])

  const obtenerAdjuntos = async () => {
    let imagenes
    if (idPublicacion > 0) {
      imagenes = await publicacionesService.buscarAdjuntos(idPublicacion)
    } else {
      imagenes =
        await publicacionesTemporalesService.buscarAdjuntos(idPublicacionTemporal)
    }
    obtenerImagen(imagenes.data.sort((x, y) => x.orden - y.orden))
  }

  const obtenerImagen = async (adjuntosData) => {
    if (!adjuntosData?.length) {
      setAdjuntos([])
      return
    }

    const imagenes = await Promise.all(
      adjuntosData.map(async (a) => {
        const { data: imagen } =
          idPublicacion > 0
            ? await publicacionesService.BuscarImagenPorAdjunto(
                parseInt(localStorage.getItem('idUsuario')),
                idPublicacion,
                a.idAdjunto
              )
            : await publicacionesTemporalesService.BuscarImagenPorAdjunto(
                parseInt(localStorage.getItem('idUsuario')),
                idPublicacionTemporal,
                a.idAdjunto
              )

        return {
          idAdjunto: a.idAdjunto,
          url: URL.createObjectURL(imagen),
          orden: a.orden,
          nombre: a.nombre
        }
      })
    )

    setAdjuntos(imagenes)
  }

  const onDrop = (acceptedFiles) => {
    acceptedFiles.forEach((archivo) => {
      const fileNameWithoutExt = archivo.name.replace(/\.[^/.]+$/, '')
      if (
        adjuntos.find((a) => {
          const adjuntoNameWithoutExt = a.nombre.replace(/\.[^/.]+$/, '')
          return (
            adjuntoNameWithoutExt === fileNameWithoutExt ||
            a.url.includes(fileNameWithoutExt)
          )
        })
      ) {
        toast.warn('El archivo ya existe en la galería.')
        return
      }
      upload(idPublicacion, idPublicacionTemporal, archivo, adjuntos.length + 1)
    })
    setIsOverDropZone(false)
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDragOver: () => setIsOverDropZone(true),
    onDragLeave: () => setIsOverDropZone(false),
    accept,
    noClick: true,
    noKeyboard: true
  })

  const upload = async (idPublicacion, idPublicacionTemporal, archivo, orden) => {
    try {
      const datosForm = new FormData()
      datosForm.append('nombre', archivo.name)
      datosForm.append('idPublicacion', idPublicacion)
      datosForm.append('idPublicacionTemporal', idPublicacionTemporal)
      datosForm.append('registroAddUsr', parseInt(localStorage.getItem('idUsuario')))
      datosForm.append('archivo', archivo)
      datosForm.append('orden', orden)

      if (idPublicacion > 0) {
        await publicacionesService.subirAdjunto(datosForm)
      } else {
        await publicacionesTemporalesService.subirAdjunto(datosForm)
      }
      setReload(true)
    } catch (error) {
      const mensaje = `Ocurrió un error al subir el archivo.`
      toastErrorLog(error, mensaje, 'adjunto')
    }
  }

  const borrarArchivo = async (e, idAdjunto) => {
    try {
      const adjuntoEliminar = adjuntos.find((a) => a.idAdjunto === idAdjunto)
      if (adjuntoEliminar) {
        const ordenEliminado = adjuntoEliminar.orden
        const nuevosAdjuntos = adjuntos
          .filter((a) => a.idAdjunto !== idAdjunto)
          .map((a) => (a.orden > ordenEliminado ? { ...a, orden: a.orden - 1 } : a))
        setAdjuntos(nuevosAdjuntos)
        await archivoAdjuntoService.modificarOrdenImagenes(nuevosAdjuntos)
      }
      if (idPublicacion > 0) {
        await publicacionesService.eliminarAdjunto(
          parseInt(localStorage.getItem('idUsuario')),
          idPublicacion,
          idAdjunto
        )
      } else {
        await publicacionesTemporalesService.eliminarAdjunto(
          parseInt(localStorage.getItem('idUsuario')),
          idPublicacionTemporal,
          idAdjunto
        )
      }
      setReload(true)
    } catch (error) {
      const mensaje = `Ocurrió un error al borrar el archivo.`
      toastErrorLog(error, mensaje, 'adjunto')
    }
  }

  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDropGallery = async (index, e) => {
    e.preventDefault()
    if (draggedIndex === null) {
      return
    }

    const newAdjuntos = [...adjuntos]
    const [moved] = newAdjuntos.splice(draggedIndex, 1)
    newAdjuntos.splice(index, 0, moved)

    const updatedAdjuntos = newAdjuntos.map((a, idx) => ({
      ...a,
      orden: idx + 1
    }))
    setAdjuntos(updatedAdjuntos)
    setDraggedIndex(null)

    await archivoAdjuntoService.modificarOrdenImagenes(updatedAdjuntos)
  }

  return (
    <>
      <div
        {...getRootProps()}
        className={`${styles.uploadZone} ${isOverDropZone ? styles.active : ''}`}
        onClick={open}
      >
        <i className="fa fa-camera fa-2x" aria-hidden="true" />
        <input {...getInputProps()} />
        <p>
          {isDraggingFile ? '📸 Arrastralas acá...' : 'Agregalas desde tu computadora'}
        </p>
      </div>

      <div className={styles.gallery}>
        {adjuntos.map((a, index) => (
          <div
            key={a.idAdjunto}
            className={styles.imageCol}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDropGallery(index, e)}
          >
            <div className={styles.imageWrapper}>
              <LinkButton
                onClick={(e) => borrarArchivo(e, a.idAdjunto)}
                className={styles.deleteBtn}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </LinkButton>
              <img
                alt={`img-${index}`}
                src={sanitizeImageUrl(a.url)}
                className={styles.uploadedImg}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

Upload.propTypes = {
  idPublicacion: PropTypes.number.isRequired,
  idPublicacionTemporal: PropTypes.number.isRequired,
  accept: PropTypes.object.isRequired
}

export default Upload
